import { Component, OnInit, OnDestroy, inject, DestroyRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormGroup, FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { CardModule } from 'primeng/card';
import { InputNumber } from 'primeng/inputnumber';
import { ButtonModule } from 'primeng/button';
import { Toast } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { Subject, distinctUntilChanged, filter, map, takeUntil } from 'rxjs';
import { FilingFormStateService } from '../../../services/mbrs/filing-form-state.service';
import { CrossReferenceService } from '../../../services/mbrs/cross-reference.service';
import { EnvironmentService } from '../../../services/common/environment.service';
import { MappingStatusService } from '../../../services/mbrs/mapping-status.service';
import { FORM_AUTOSAVE_DEBOUNCE_MS } from '../../../utils/debounce-times.util';
import { buildEditModeLivePipeline } from '../../../utils/form-edit-pipeline.util';
import { pickSubset } from '../../../utils/pick-subset.util';
import { adoptIntoInlineEditSheet } from '../../../utils/adopt-remote-sheet-state.util';
import { isRowVisible } from '../../../utils/section-visibility.util';
import { getSchemaMin, mapSchemaValidators, valueBelowSchemaMin } from '../../../utils/schema-validators.util';
import { SubmitForValidationComponent } from "../../common/submit-for-validation/submit-for-validation.component";
import { RichTextFieldComponent } from '../../common/rich-text-field/rich-text-field.component';
import { NegativeHighlightDirective } from '../../../directives/negative-highlight.directive';
import { SchemaNumericDirective } from '../../../directives/schema-numeric.directive';
import { NumericFieldPolicy, resolveNumericPolicy } from '../../../utils/numeric-policy.util';
import { SkeletonModule } from 'primeng/skeleton';
import {
  pickRole23SchemaFile,
  ROLE23_SHARE_CLASS_MEMBERS,
} from '../../../services/mbrs/validators/role23-notes-issued-capital.rules';
import { SHEET_KEYS } from '../../../models/sheet-keys';
import { isCellVisible } from '../../../services/mbrs/validators/dimension-visibility.util';
import {
  TOAST_DURATION_DEFAULT_MS,
} from '../../../utils/toast-durations.util';
import { PlaceholderPipe } from '../../../utils/pipes/placeholder.pipe';
import { RequiredMarkerComponent } from '../../common/required-marker/required-marker.component';
import { FieldLinkLabelComponent } from '../../common/field-link-label/field-link-label.component';
import { isControlMandatory } from '../../../utils/required-field.util';

import { SheetLoaderComponent } from '../../common/sheet-loader/sheet-loader.component';
import { FieldLinkTargetDirective, FieldLinkSectionSink } from '../../../directives/field-link-target.directive';
import { computedControlPredicate, revealValidationErrors, enableAndRevealErrors } from '../../../utils/validation-reveal.util';
import {
  LoadTrace,
  NO_OP_LOAD_TRACE,
  SheetLoadTraceService,
} from '../../../services/mbrs/sheet-load-trace.service';
import { SHEET_LOAD_STEPS } from '../../../utils/sheet-load-trace.util';
import {
  classifySheetInitError,
  fetchSheetSchema,
} from '../../../utils/schema-asset.util';
interface SchemaControl {
  qname: string;
  label: string;
  type: string;
  ui: {
    control: string;
    level?: number;
    note?: string;
    rows?: number;
    options?: string[];
    conditionalOn?: string;
    conditionalValue?: string;
    placeholder?: string;
    tableColumn?: boolean;
    readonly?: boolean;
    calculated?: boolean;
    useGrouping?: boolean;           // Show thousand separator (comma) for number inputs
    positiveWholeNumber?: boolean;   // Apply positive whole number directive (strips negative, removes decimals)
    linkTo?: {                  // NEW
      role: string;             // Role code (e.g., "210100")
      qname: string;            // Target qname in source role
    };
    inheritFromRole9?: boolean; // LEGACY (keep for backward compatibility)
    inheritFromRole?: string;   // LEGACY
  };
  required?: boolean;
  validators?: Array<{ name: string; value?: any }>;
  customValidations?: Array<{   // NEW (Optional)
    type: string;
    message: string;
    rule?: any;
    condition?: any;
  }>;
}

interface FormSchema {
  role: string;
  sheetId: string;
  label: string;
  description: string;
  controls: SchemaControl[];
}

@Component({
  selector: 'app-role23-740000-notes-issued-capital',
  standalone: true,
  imports: [
    SheetLoaderComponent,
    RequiredMarkerComponent,
    FieldLinkLabelComponent,
    CommonModule,
    ReactiveFormsModule,
    CardModule,
    InputNumber,
    ButtonModule,
    Toast,
    SubmitForValidationComponent,
    RichTextFieldComponent,
    NegativeHighlightDirective,
    SchemaNumericDirective,
    SkeletonModule,
    PlaceholderPipe,
    FieldLinkTargetDirective
  ],
  providers: [MessageService],
  templateUrl: './role23-740000-notes-issued-capital.component.html',
  styleUrls: ['./role23-740000-notes-issued-capital.component.css']
})

export class Role23740000NotesIssuedCapitalComponent implements OnInit, OnDestroy {
  private stateService = inject(FilingFormStateService);
  private crossRefService = inject(CrossReferenceService);
  private messageService = inject(MessageService);
  private loadTrace = inject(SheetLoadTraceService);
  /**
   * This mount's load trace. Opened as the first act of `ngOnInit` and closed
   * either by the terminal step mark or by the init catch, so a failure reports
   * which of the seven load steps this sheet actually reached.
   *
   * @see src/app/utils/sheet-load-trace.util.ts
   */
  private loadTraceHandle: LoadTrace = NO_OP_LOAD_TRACE;
  private envService = inject(EnvironmentService);
  private mappingStatus = inject(MappingStatusService);
  private destroy$ = new Subject<void>();
  private destroyRef = inject(DestroyRef);

  form!: FormGroup;
  schema?: FormSchema;
  /**
   * Card header carrying the filing's submission type, e.g.
   * "Notes - Issued Capital (FS-MPERS)".
   *
   * Two rules, both of which this sheet used to break:
   *  - the name comes from the canonical `SHEET_DISPLAY_NAMES` map (via
   *    `getSheetTitleWithSubmissionType`), never a literal repeated here;
   *  - it is a LIVE getter, never an `ngOnInit` snapshot — see the cold-mount
   *    rule on `FilingFormStateService.getFormTitleWithSubmissionType()`.
   */
  get formTitle(): string {
    return this.stateService.getSheetTitleWithSubmissionType(
      SHEET_KEYS.notesIssuedCapital
    );
  }

  get isDev(): boolean {
    return this.envService.isDevelopment();
  }

  isLoading = true;
  private isLoadingState = false;
  isValidationComplete = false;

  /**
   * True once the initial load (schema → buildForm → loadSavedState → baseline
   * snapshot) has fully settled. After this point setupStateListener() stops
   * re-patching the form so a self-originated state$ emission — e.g. the user
   * ticking ONE row (saveEditMode → updateSheetData) — can no longer clobber the
   * typed-but-unsaved values of OTHER rows that are mid-edit (those edits live
   * only in the form; live edits are intentionally never persisted). Mirrors
   * role24's guard for the identical dual-table notes shape.
   */
  private isInitialLoadComplete = false;

  // Track which Level 4 sections are expanded (separate state for Consolidated and Separate)
  expandedSectionsConsolidated: { [key: string]: boolean } = {};
  expandedSectionsSeparate: { [key: string]: boolean } = {};

  /**
   * Section sinks for FieldLinkTargetDirective — write straight into the
   * records above (see FieldLinkSectionSink). One per table.
   */
  protected readonly consolidatedSectionSet: FieldLinkSectionSink = {
    add: (qname: string) => (this.expandedSectionsConsolidated[qname] = true),
  };
  protected readonly separateSectionSet: FieldLinkSectionSink = {
    add: (qname: string) => (this.expandedSectionsSeparate[qname] = true),
  };

  // Edit mode tracking per row PER DIMENSION (key: `${qname}|${Consolidated|Separate}`,
  // value: original values). Keying by dimension keeps the Consolidated and Separate
  // tables' edit state independent — clicking the pencil in one table must not flip the
  // same-named row in the other (mirrors role19's getEditKey pattern).
  private editingRows = new Map<string, { [key: string]: number | null }>();

  /** Row Mapping-Status frozen at edit-enter; while a row is mid-edit its badge
   *  shows this committed value (not the live in-edit recompute) so the pencil
   *  alone never flips it to 'Edited'. Cleared on Tick/Cancel → badge recomputes.
   *  Key = getEditKey(control, dimension): `${qname}|${dimension}` */
  private frozenRowStatus = new Map<string, 'Failed' | 'Edited' | 'Validated*' | 'Validated'>();

  constructor() {
    // Debug Log (Only enable when required to debug, do not allow this to land into prod)
    // console.log('🏗️ Role-23 Component Constructor - Component is being created');
  }

  /**
   * Get total number of actual form fields (excluding section headers)
   */
  get totalFields(): number {
    if (!this.schema) return 0;
    return this.schema.controls.filter(control => control.type !== 'SectionHeader').length;
  }

  /**
   * Check if Consolidated table should be displayed
   * Returns true if Nature of Statements is 'Consolidated', false if 'Separate'
   */
  get dimensionVisibility() {
    return this.crossRefService.getVisibility(SHEET_KEYS.notesIssuedCapital);
  }

  get showConsolidatedTable(): boolean {
    return isCellVisible(this.dimensionVisibility, 'Consolidated', 'CurrentYear');
  }

  /**
   * Formatted current financial-year date range for the dimension-table header
   * (e.g. "01/01/2024 - 31/12/2024"). Sourced from the Filing Information (Role-1)
   * DEI block via the shared CrossReferenceService — the single source of truth
   * used by Role 19 (SOCIE) so the period label stays consistent across sheets.
   */
  get periodHeaderCurrent(): string {
    return this.crossRefService.getFormattedFinancialYear('current');
  }

  async ngOnInit(): Promise<void> {
    // Open this mount's load trace before anything else, so a failure
    // anywhere below reports which load steps were reached.
    this.loadTraceHandle = this.loadTrace.beginSheetLoad(
      '23',
      this.stateService.getConversionId(),
      this.destroyRef,
    );
    // Debug Log (Only enable when required to debug, do not allow this to land into prod)
    // console.log('========================================');
    // console.log('ROLE-23 COMPONENT LOADED - VERSION 3.0');
    // console.log('Standard Bracket Format (Standardized)');
    // console.log('========================================');
    // console.log('🚀 Role-23 ngOnInit - Starting initialization...');
    try {
      // Debug Log (Only enable when required to debug, do not allow this to land into prod)
      // console.log('📋 Loading schema...');
      
      await this.loadSchema();

      // Debug Log (Only enable when required to debug, do not allow this to land into prod)
      // console.log('✅ Schema loaded:', this.schema);

      // Debug Log (Only enable when required to debug, do not allow this to land into prod)
      // console.log('📂 Initializing expanded sections...');
      
      this.initializeExpandedSections();

      // Debug Log (Only enable when required to debug, do not allow this to land into prod)
      // console.log('🔨 Building form...');
      
      this.buildForm();
      this.loadTraceHandle?.mark(SHEET_LOAD_STEPS.formBuilt);
      
      // Debug Log (Only enable when required to debug, do not allow this to land into prod)
      // console.log('✅ Form built. Control count:', Object.keys(this.form.controls).length);
      // console.log('✅ Form structure sample:', Object.keys(this.form.controls).slice(0, 5));

      // Debug Log (Only enable when required to debug, do not allow this to land into prod)
      // console.log('💾 Loading saved state...');
      this.loadSavedState();
      this.loadTraceHandle?.mark(SHEET_LOAD_STEPS.stateRestored);

      // Debug Log (Only enable when required to debug, do not allow this to land into prod)
      // console.log('🔄 Setting up state persistence...');
      this.setupStatePersistence();
      this.setupTextBlockPersistence();

      // Debug Log (Only enable when required to debug, do not allow this to land into prod)
      // console.log('👂 Setting up state listener...');
      this.setupStateListener();
      this.setupRemoteUpdateAdoption();

      // Debug Log (Only enable when required to debug, do not allow this to land into prod)
      // console.log('🔧 Setting up conditional field logic...');
      this.setupConditionalFieldLogic();

      // Debug Log (Only enable when required to debug, do not allow this to land into prod)
      // console.log('📝 Updating form title...');

      // Reveal the sheet's already-computed errors now that the form exists and
      // has been populated. Deterministic replacement for the filing-form
      // container's setTimeout-driven markAllAsTouched, which silently no-ops
      // when a sheet's async init has not yet assigned its FormGroup — see
      // revealValidationErrors' docblock. Touch-only: cannot move the verdict.
      revealValidationErrors(this.form, {
        isComputed: computedControlPredicate(this.schema?.controls),
      });

      this.isLoading = false;
      this.loadTraceHandle?.mark(SHEET_LOAD_STEPS.finalized);
      // Debug Log (Only enable when required to debug, do not allow this to land into prod)
      // console.log('✅ Role-23 initialization complete!');
    } catch (error) {
      console.error('❌ Role-23 initialization error:', error);
      // Report through the trace instead of a fixed sentence. The toast now
      // names this sheet, the steps it reached, and whether the schema was
      // missing, unreadable, or fine but the form could not be built —
      // `classifySheetInitError` infers the last of those by elimination.
      // Must run BEFORE the loading flag is cleared below: `fail()` closes the
      // trace, which is what stops the finalize mark on the next line from
      // recording this failed init as a success.
      this.loadTraceHandle.fail(classifySheetInitError(error), error);
      this.isLoading = false;
      this.loadTraceHandle?.mark(SHEET_LOAD_STEPS.finalized);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Cross-tab safety net — sibling-tab edits to this sheet's own state must
   * repaint here too. Role-23 has no cross-sheet validation, so the tail is
   * just `evaluateSheetStatus()`.
   */
  private setupRemoteUpdateAdoption(): void {
    this.stateService.remoteUpdateApplied$
      .pipe(
        filter(({ streamKey }) => streamKey === 'modifiedState'),
        filter(() => !this.stateService.isSheetInitializing(SHEET_KEYS.notesIssuedCapital)),
        filter(() => !!this.schema && !!this.form),
        takeUntil(this.destroy$)
      )
      .subscribe(({ sheetKey, changedSheets }) => {
        if (this.envService.isCrossTabSyncDebugEnabled()) {
          // console.log('[CrossTabSync][remote-validation-triggered]', {
          //   receiver: 'role23-notesIssuedCapital',
          //   sourceSheetKey: sheetKey,
          // });
        }

        if (changedSheets.includes(SHEET_KEYS.notesIssuedCapital))
          this.adoptRemoteState();

        this.evaluateSheetStatus();
      });
  }

  /**
   * Cross-tab adoption for role23 (notes issued capital). Rows currently open
   * in edit mode are skipped — the user's in-flight work wins until they tick
   * (local overwrites and broadcasts) or press X (the row lands on the
   * adopted value; see `cancelEditMode`).
   *
   * MUST NOT write back to state — see the invariant on `adoptIntoInlineEditSheet`.
   *
   * Developer Note (TODO): silent adoption is the P.O. decision of 2026-08-17 and
   * is revisitable. If feedback is later wanted, the two options considered were a
   * brief per-row highlight (preferred — it answers "why did that number change?"
   * at the spot it changed) and a per-adoption toast (cheaper, but says nothing
   * about WHAT changed and stacks on repeated sibling edits).
   */
  private adoptRemoteState(): void {
    const openControlNames = [...this.editingRows.values()]
      .flatMap(snapshot => Object.keys(snapshot ?? {}));

    adoptIntoInlineEditSheet(
      this.form,
      this.stateService.getSheetData(SHEET_KEYS.notesIssuedCapital),
      openControlNames
    );
  }

  /**
   * Setup listener for external state changes (e.g., data loaded from backend)
   * This allows the form to react when the container populates data via the state service
   */
  private setupStateListener(): void {
    this.stateService.state$
      .pipe(
        map((state) => state.notesIssuedCapital),
        distinctUntilChanged(
          (prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)
        ),
        takeUntil(this.destroy$)
      )
      .subscribe((notesIssuedCapitalData) => {
        // Debug Log (Only enable when required to debug, do not allow this to land into prod)
        // console.log('🔄 Role-23 setupStateListener - Received data:', notesIssuedCapitalData);

        if (notesIssuedCapitalData && Object.keys(notesIssuedCapitalData).length > 0) {
          // After initial load completes, skip re-patching. Past this point the
          // only emissions on this slice are the user's own commits (saveEditMode
          // → updateSheetData) and a re-patch would overwrite the typed-but-
          // unsaved values of OTHER rows still mid-edit (those edits live only in
          // the form). Mirrors role24. See isInitialLoadComplete.
          if (this.isInitialLoadComplete) {
            return;
          }

          // Temporarily set isLoadingState to prevent auto-save during external data load
          this.isLoadingState = true;

          // Debug: Check for specific Beginning field
          const beginningKeys = Object.keys(notesIssuedCapitalData).filter(key => key.includes(':Beginning'));
          if (beginningKeys.length > 0) {
            // Debug Log (Only enable when required to debug, do not allow this to land into prod)
            // console.log('🔍 Found Beginning fields in data:', beginningKeys);
            // beginningKeys.forEach(key => {
            //   console.log(`   - ${key}:`, notesIssuedCapitalData[key]);
            // });
          }

          // Debug: Show what form controls exist for Beginning field
          const formControlKeys = Object.keys(this.form.controls).filter(key => key.includes(':Beginning'));
          // Debug Log (Only enable when required to debug, do not allow this to land into prod)
          // if (formControlKeys.length > 0) {
          //   console.log('🔍 Form controls for Beginning fields:', formControlKeys);
          // }

          // Debug Log (Only enable when required to debug, do not allow this to land into prod)
          // console.log('📝 Patching form from state listener...');

          // NO transformation needed - data is already transformed in filing-form-container
          // Patch the form with the data (already in frontend format)
          this.form.patchValue(notesIssuedCapitalData, { emitEvent: false });

          // Debug Log (Only enable when required to debug, do not allow this to land into prod)
          // console.log('✅ Form value after patch from listener:', this.form.value);

          // Debug Log (Only enable when required to debug, do not allow this to land into prod)
          // Check Beginning field values after patch
          // if (formControlKeys.length > 0) {
          //   console.log('🔍 Form values for Beginning fields after patch:');
          //   formControlKeys.forEach(key => {
          //     const control = this.form.get(key);
          //     console.log(`   - ${key}:`, control?.value);
          //   });
          // }

          // Re-trigger calculations after data load
          // This ensures Total columns are recalculated from loaded data
          this.triggerAllCalculations();

          // Re-enable auto-save after a short delay
          setTimeout(() => {
            this.isLoadingState = false;
          }, 100);
        }
      });
  }

  /**
   * REMOVED: triggerAllCalculations() - Total fields are now user-editable, not auto-calculated
   * This method is no longer needed as totals are manually entered by users
   */
  private triggerAllCalculations(): void {
    // No-op: Total fields are user-editable, no calculations needed
  }

  /**
   * Seed EVERY section header (all levels) as expanded by default, for both the
   * Consolidated and Separate tables. Seeding every level — not just level 4 —
   * is required so the first toggle click flips a real `true → false`: an
   * unseeded section is `undefined`, and `toggleSection*` computes `!undefined`
   * = `true` (a no-op that leaves the section expanded), so the first click on
   * an unseeded level-5 header used to appear dead. `isSectionExpanded*` already
   * defaults `undefined` to expanded, so this only makes the toggle consistent.
   */
  private initializeExpandedSections(): void {
    if (!this.schema) return;

    this.schema.controls.forEach(control => {
      if (control.type === 'SectionHeader') {
        this.expandedSectionsConsolidated[control.qname] = true;
        this.expandedSectionsSeparate[control.qname] = true;
      }
    });
  }

  /**
   * Toggle expand/collapse state for a Level 4 section in Consolidated table
   */
  toggleSectionConsolidated(sectionQname: string): void {
    this.expandedSectionsConsolidated[sectionQname] = !this.expandedSectionsConsolidated[sectionQname];
  }

  /**
   * Toggle expand/collapse state for a Level 4 section in Separate table
   */
  toggleSectionSeparate(sectionQname: string): void {
    this.expandedSectionsSeparate[sectionQname] = !this.expandedSectionsSeparate[sectionQname];
  }

  /**
   * Check if a Consolidated section is expanded
   */
  isSectionExpandedConsolidated(sectionQname: string): boolean {
    return this.expandedSectionsConsolidated[sectionQname] !== false;
  }

  /**
   * Check if a Separate section is expanded
   */
  isSectionExpandedSeparate(sectionQname: string): boolean {
    return this.expandedSectionsSeparate[sectionQname] !== false;
  }

  /**
   * Check if a row should be shown in the Consolidated table based on its
   * ancestor sections' expand/collapse state.
   *
   * Delegates to the shared, pure `isRowVisible` helper so that EVERY section
   * header (any level) governs its descendants — fixing the prior logic that
   * only honoured level-4 sections. Symptoms of that bug: collapsing a level-4
   * header left its level-5 child headers orphaned (header rows were never
   * gated), and toggling a level-5 header flipped its chevron but hid nothing
   * (its rows only consulted the level-4 ancestor). Gates headers too (the
   * template now calls this for level 3/4/5 header rows), so the early
   * `!tableColumn` short-circuit is gone. `isSectionExpandedConsolidated`
   * defaults un-collapsed sections to expanded, so no seeding is required.
   */
  shouldShowRowConsolidated(control: SchemaControl, index: number): boolean {
    if (!this.schema) return true;
    return isRowVisible(
      this.schema.controls,
      index,
      (qname) => this.isSectionExpandedConsolidated(qname),
      (control) => control.type === 'SectionHeader'
    );
  }

  /**
   * Check if a row should be shown in the Separate table based on its ancestor
   * sections' expand/collapse state. Mirror of `shouldShowRowConsolidated`
   * against the Separate table's independent expansion state — see that method
   * for the rationale.
   */
  shouldShowRowSeparate(control: SchemaControl, index: number): boolean {
    if (!this.schema) return true;
    return isRowVisible(
      this.schema.controls,
      index,
      (qname) => this.isSectionExpandedSeparate(qname),
      (control) => control.type === 'SectionHeader'
    );
  }

  /**
   * Load schema from JSON file (dynamically selects MFRS or MPERS)
   */
  private async loadSchema(): Promise<void> {
    // Wait for retrieve-mbrs-data hydration so the accounting standard is
    // populated before schema selection — a mid-hydration refresh mount would
    // otherwise fall back to the MFRS default and MPERS values would never bind
    // (blank form).
    await this.stateService.waitForMbrsHydration('role23');
    this.loadTraceHandle.mark(SHEET_LOAD_STEPS.hydrationSettled);

    const filingData = this.stateService.getActiveFilingInfoData();
    const accountingStandard = filingData['ssmt-dei:BasisOfAccountingStandardsAppliedToPrepareFinancialStatements'] || '';

    // Variant selection is owned by role23-notes-issued-capital.rules.ts so
    // the pure validator and this component resolve identically.
    const schemaFile = pickRole23SchemaFile(accountingStandard);

    this.loadTraceHandle.mark(SHEET_LOAD_STEPS.schemaSelected);
    this.schema = await fetchSheetSchema<FormSchema>(
      this.loadTraceHandle,
      `/assets/forms/${schemaFile}`,
    );
    this.loadTraceHandle.mark(SHEET_LOAD_STEPS.schemaParsed);
  }

  /**
   * Build reactive form from schema
   */
  private buildForm(): void {
    const formControls: any = {};

    if (this.schema && this.schema.controls) {
      this.schema.controls.forEach(control => {
        if (control.type === 'SectionHeader') {
          return;
        }

        if (control.type === 'TextBlock') {
          const defaultValue = this.getDefaultValue(control);
          const validators: any[] = this.getValidators(control);
          formControls[control.qname] = new FormControl(defaultValue, validators);
          return;
        }

        // Table column fields get 16 controls each using STANDARD BRACKET FORMAT
        // Pattern: {qname}:member[Consolidated|Separate][CurrentYear|PreviousYear]
        // This matches the standardized format output by the transformer service
        if (control.ui.tableColumn) {
          const defaultValue = this.getDefaultValue(control);
          const validators: any[] = this.getValidators(control);

          // All controls start as disabled by default (edit mode will enable them)
          const startsDisabled = true;

          // Share class members to create (Total uses base qname without member).
          // Sourced from role23-notes-issued-capital.rules so the pure validator's
          // buildRowColumnKeys() and this expansion stay in lockstep.
          const members = ROLE23_SHARE_CLASS_MEMBERS;

          // Create 16 FormControls (4 members × 2 dimensions × 2 periods)
          members.forEach(member => {
            const baseKey = member ? `${control.qname}:${member}` : control.qname;

            // Consolidated CurrentYear
            formControls[`${baseKey}[Consolidated][CurrentYear]`] = new FormControl(
              { value: defaultValue, disabled: startsDisabled },
              validators
            );

            // Consolidated PreviousYear
            formControls[`${baseKey}[Consolidated][PreviousYear]`] = new FormControl(
              { value: defaultValue, disabled: startsDisabled },
              validators
            );

            // Separate CurrentYear
            formControls[`${baseKey}[Separate][CurrentYear]`] = new FormControl(
              { value: defaultValue, disabled: startsDisabled },
              validators
            );

            // Separate PreviousYear
            formControls[`${baseKey}[Separate][PreviousYear]`] = new FormControl(
              { value: defaultValue, disabled: startsDisabled },
              validators
            );
          });
        }
      });
    }

    this.form = new FormGroup(formControls);
  }

  /**
   * Get default value for control based on type
   */
  private getDefaultValue(control: SchemaControl): any {
    switch (control.type) {
      case 'YesNoItemType':
        return null;
      case 'IntegerItemType':
      case 'MonetaryItemType':
        return null;
      case 'TextBlock':
      case 'StringItemType':
      default:
        return '';
    }
  }

  /**
   * Get validators for control.
   *
   * The narrative TextBlock gets `Validators.required` (table-column
   * required-ness is checked manually in rowHasBlockingError because
   * PreviousYear cells are intentionally unvalidated). Numeric table columns
   * get their schema-declared numeric constraints — the `min: 0` on the integer
   * share-count rows is re-enforced here via the shared mapSchemaValidators()
   * util so a negative share count is a blocking error (the XBRL value can never
   * be negative). Monetary columns declare no `min`, so they keep accepting
   * negatives. (Earlier this method dropped all min validators "to allow
   * negative values"; that let a "-" be typed into the share-count fields the
   * schema marks non-negative — the bug this restores.)
   */
  private getValidators(control: SchemaControl): any[] {
    const validators: any[] = [];

    // Add required validator for TextBlock fields with required: true
    if (control.required && control.type === 'TextBlock') {
      validators.push(Validators.required);
    }

    // Re-enforce schema-declared numeric constraints (min/max/pattern) on table
    // columns — granular, per-control (see schema-validators.util).
    if (control.ui.tableColumn) {
      validators.push(...mapSchemaValidators(control));
    }

    return validators;
  }

  /**
   * Declared non-negative lower bound for a control, or `undefined`. Used for
   * the `Minimum value: ${this.schemaMin(control)}` message in
   * `getRowErrorDescription`.
   *
   * This method no longer feeds a `[min]` binding on `<p-inputnumber>` — that
   * responsibility moved to `SchemaNumericDirective` (`appSchemaNumeric`,
   * applied on every table-column cell in the template), which pushes
   * `min`/`max` directly onto the PrimeNG instance rather than through a
   * template binding. Reason: PrimeNG's `InputNumber.min`/`.max` `@Input()`s
   * are declared with Angular's `numberAttribute` transform, whose fallback
   * for a non-numeric value (i.e. `undefined`, the correct value for a
   * monetary column with no declared bound) is `NaN`, not `undefined` — a
   * bound `[min]="schemaMin(control)"` would coerce every unconstrained
   * column's min to `NaN` on every change-detection cycle, which both renders
   * `aria-valuemin="NaN"` and silently breaks `allowMinusSign()` (blocking the
   * `-` keystroke on monetary fields). See `SchemaNumericDirective`'s class
   * docblock for the full mechanism.
   */
  schemaMin(control: SchemaControl): number | undefined {
    return getSchemaMin(control);
  }

  /** Memoised per-control policy — templates call this once per binding. */
  private policyCache = new Map<string, NumericFieldPolicy>();

  numericPolicy(control: SchemaControl): NumericFieldPolicy {
    const cached = this.policyCache.get(control.qname);
    if (cached)
      return cached;
    const resolved = resolveNumericPolicy(control);
    this.policyCache.set(control.qname, resolved);
    return resolved;
  }

  /**
   * Setup conditional field logic
   */
  private setupConditionalFieldLogic(): void {
    // Add any conditional logic here if needed
  }

  /**
   * REMOVED: setupCalculations() - Total fields are now user-editable, not auto-calculated
   * The Total column is a user input field using the base qname without share class dimension
   */

  /**
   * Check if field should be displayed based on conditional logic
   */
  shouldDisplayField(control: SchemaControl): boolean {
    if (!control.ui.conditionalOn) {
      return true;
    }

    const parentControl = this.form?.get(control.ui.conditionalOn);
    if (!parentControl) {
      return true;
    }

    return parentControl.value === control.ui.conditionalValue;
  }

  /**
   * Load saved state from service
   */
  private loadSavedState(): void {
    this.isLoadingState = true;
    this.stateService.markSheetInitializing(SHEET_KEYS.notesIssuedCapital);
    const savedData = this.stateService.getSheetData(SHEET_KEYS.notesIssuedCapital);

    // Debug Log (Only enable when required to debug, do not allow this to land into prod)
    // console.log('🔍 Role-23 loadSavedState - Data from state service:', savedData);
    // console.log('🔍 Role-23 loadSavedState - Form structure:', this.form.value);

    if (savedData && Object.keys(savedData).length > 0) {
      // Debug Log (Only enable when required to debug, do not allow this to land into prod)
      // console.log('📝 Patching form with data...');

      this.form.patchValue(savedData, { emitEvent: false });

      // Debug Log (Only enable when required to debug, do not allow this to land into prod)
      // console.log('✅ Form value after patch:', this.form.value);
    } else {
      // Debug Log (Only enable when required to debug, do not allow this to land into prod)
      // console.log('⚠️ No saved data found');
    }

    // Small delay to ensure form is fully initialized before enabling auto-save
    setTimeout(() => {
      this.isLoadingState = false;
      this.isValidationComplete = true;

      // Snapshot baseline after all debounced valueChanges settle
      setTimeout(() => {
        this.evaluateSheetStatus();
        this.stateService.markSheetInitialized(SHEET_KEYS.notesIssuedCapital);
        // Initial load has settled — from here the state listener stops
        // re-patching so self-originated commits can't clobber in-edit rows.
        this.isInitialLoadComplete = true;
      }, 600);
    }, 100);
  }

  /**
   * Setup automatic state persistence.
   * Uses buildEditModeLivePipeline (no persist callback) so live edits do NOT
   * auto-save. Commit happens only on Tick (saveEditMode) or TextBlock Save
   * (setupTextBlockPersistence).
   */
  private setupStatePersistence(): void {
    buildEditModeLivePipeline({
      source$: this.form.valueChanges,
      debounceMs: FORM_AUTOSAVE_DEBOUNCE_MS,
      destroyRef: this.destroyRef,
      shouldSkip: () => this.isLoadingState,
      validateSameSheet: () => { /* role23 has no live numeric validator; status only */ },
      evaluateStatus: () => this.evaluateSheetStatus(),
    });
  }

  /**
   * Persist TextBlock (rich-text) fields on their own valueChanges. A rich-text
   * field only emits when the user clicks Save in its popup, so each emission is
   * an explicit commit. Subset-persist just that field's qname.
   */
  private setupTextBlockPersistence(): void {
    if (!this.schema) return;
    for (const control of this.schema.controls) {
      if (control.type !== 'TextBlock') continue;
      const fc = this.form.get(control.qname);
      if (!fc) continue;
      const qname = control.qname;
      fc.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
        if (this.isLoadingState) return;
        this.stateService.updateSheetData(SHEET_KEYS.notesIssuedCapital, pickSubset(this.form.getRawValue(), [qname]));
        this.evaluateSheetStatus();
      });
    }
  }

  /**
   * Auto-fill demo data
   */
  autoFillDemo(): void {
    if (!this.schema) return;

    const demoData: any = {};

    for (const control of this.schema.controls) {
      if (control.type === 'TextBlock') {
        const cleanLabel = control.label.replace(' [text block]', '');
        demoData[control.qname] = `This is sample disclosure content for ${cleanLabel}.\n\nThe note would contain detailed information about ${cleanLabel.toLowerCase()}.\n\nThis content is auto-filled for demonstration purposes only.`;
      }
    }

    this.form.patchValue(demoData);

    this.messageService.add({
      severity: 'success',
      summary: 'Demo Data Loaded',
      detail: 'Filled note disclosure with sample data',
      life: TOAST_DURATION_DEFAULT_MS
    });
  }

  /**
   * Clear form
   */
  clearForm(): void {
    if (confirm('Are you sure you want to clear all data in this form?')) {
      this.form.reset();
      this.stateService.clearSheetData(SHEET_KEYS.notesIssuedCapital);

      this.messageService.add({
        severity: 'info',
        summary: 'Form Cleared',
        detail: 'All form data has been cleared',
        life: TOAST_DURATION_DEFAULT_MS
      });
    }
  }

  /**
   * Submit form
   */
  submitForm(): void {
    if (this.form.invalid) {
      // Mark all controls as touched to show validation errors
      Object.keys(this.form.controls).forEach(key => {
        const control = this.form.get(key);
        if (control) {
          control.markAsTouched();
        }
      });

      this.messageService.add({
        severity: 'warn',
        summary: 'Validation Error',
        detail: 'Please fill in all required fields correctly',
        life: TOAST_DURATION_DEFAULT_MS
      });
      return;
    }

    const formData = this.form.value;

    this.messageService.add({
      severity: 'success',
      summary: 'Form Submitted',
      detail: 'Issued capital note has been saved successfully',
      life: TOAST_DURATION_DEFAULT_MS
    });
  }

  /**
   * Get form control by qname
   */
  /**
   * Does this row's label get a `*`?
   *
   * Delegates to the shared `isControlMandatory` so this sheet cannot drift from
   * the other 16 — the marker used to live only inside
   * `<label class="field-label">`, which this sheet does not render, so its
   * mandatory rows went unmarked from the initial scaffolding until 2026-08-11.
   * See `isControlMandatory` in `required-field.util.ts`.
   *
   * `crossSheetValue` is supplied because a cross-sheet `requiredIf` degrades to
   * "not mandatory" without it; every other declaration form this sheet uses is
   * unconditional and needs no context. Same-sheet `selfData` is deliberately
   * NOT passed: this sheet keys its values per cell
   * (`qname[Dimension][Period]`), not by bare qname, so a same-sheet lookup
   * would silently miss. Add it — with the right key shape — if a same-sheet
   * conditional rule is ever declared here.
   */
  isFieldRequired(control: any): boolean {
    return isControlMandatory(control, {
      crossSheetValue: (sheet, field) =>
        (this.stateService.getSheetData(sheet as any) as any)?.[field],
    });
  }

  getControl(qname: string): any {
    return this.form.get(qname);
  }

  /**
   * Check if a control is inside the table section
   */
  isInsideTableSection(control: SchemaControl): boolean {
    if (!this.schema) return false;

    const currentIndex = this.schema.controls.findIndex(candidate => candidate.qname === control.qname);
    if (currentIndex === -1) return false;

    if (control.type === 'SectionHeader' &&
        control.ui.level === 2 &&
        control.label === 'Disclosure on issued capital') {
      return false;
    }

    return true;
  }

  /**
   * Generate form control key in STANDARD BRACKET FORMAT
   * Format: {qname}:member[Consolidated|Separate][CurrentYear|PreviousYear]
   * For Total: {qname}[Consolidated|Separate][CurrentYear|PreviousYear] (no member suffix)
   *
   * This matches the standardized bracket format used across all roles (8, 9, 12-18, 23, 24)
   *
   * @param qname - The XBRL qname (e.g., "ifrs-full:NumberOfSharesIssuedAndFullyPaid")
   * @param consolidatedOrSeparate - Dimension (Consolidated or Separate)
   * @param shareClass - Share class identifier ('Total', 'Ordinary', 'Redeemable', 'NonRedeemable')
   * @returns Bracket format key for form control access
   */
  getXbrlControlName(qname: string, consolidatedOrSeparate: 'Consolidated' | 'Separate', shareClass: string): string {
    // Total uses base qname without member suffix
    if (shareClass === 'Total') {
      return `${qname}[${consolidatedOrSeparate}][CurrentYear]`;
    }

    // Share class columns use member suffix (match backend casing exactly)
    const memberMap: { [key: string]: string } = {
      'Ordinary': 'OrdinarySharesMember',
      'Redeemable': 'RedeemablePreferenceSharesMember',
      'NonRedeemable': 'NonredeemablePreferenceSharesMember'  // lowercase 'r' to match backend
    };

    return `${qname}:${memberMap[shareClass]}[${consolidatedOrSeparate}][CurrentYear]`;
  }

  /**
   * Get combined validation status for a row (checking all 4 input columns including Total).
   * Delegates to MappingStatusService.computeRowStatus for shared Edited-delegation logic.
   * Priority: Edited > Failed > Validated* > Validated
   */
  getRowValidationStatus(control: SchemaControl, consolidatedOrSeparate: 'Consolidated' | 'Separate'): 'Failed' | 'Edited' | 'Validated*' | 'Validated' {
    if (!control.ui.tableColumn) return 'Validated';

    if (this.isRowInEditMode(control, consolidatedOrSeparate)) {
      const frozen = this.frozenRowStatus.get(this.getEditKey(control, consolidatedOrSeparate));
      if (frozen !== undefined) return frozen;
    }

    const inputMembers = ['Ordinary', 'Redeemable', 'NonRedeemable', 'Total'];

    const controlNames = inputMembers.map(member =>
      this.getXbrlControlName(control.qname, consolidatedOrSeparate, member)
    );

    const currentValues: Record<string, unknown> = {};
    for (const controlName of controlNames) {
      const formControl = this.form.get(controlName);
      if (formControl) {
        currentValues[controlName] = formControl.value;
      }
    }

    return this.mappingStatus.computeRowStatus({
      controlNames,
      currentValues,
      lastSubmissionValues: this.mappingStatus.getLastSubmissionValues(SHEET_KEYS.notesIssuedCapital),
      // VERDICT signal — the raw blocking-error check, NOT masked by the 'Edited'
      // display state applied via forceEdited below. See rowHasBlockingError().
      hasError: this.rowHasBlockingError(control, consolidatedOrSeparate),
      hasSuggestedChangeWarning: false, // Role23 has no API mismatch warnings
      forceEdited: false,
    });
  }

  /**
   * Whether a table row carries a real blocking error (invalid cell or empty
   * required cell) for the given dimension — the sheet-level VERDICT signal.
   *
   * Unlike {@link getRowValidationStatus} this is NOT masked by the 'Edited'
   * display state (`forceEdited` / `isRowInEditMode`): a freshly-edited row
   * still DISPLAYS as 'Edited' (pencil), but the published `hasErrors` reflects
   * the error so the row cannot pass submission while a cell is mid-edit. Keeps
   * evaluateSheetStatus() in agreement with the headless pure validator, which
   * has no edit-mode concept.
   */
  protected rowHasBlockingError(
    control: SchemaControl,
    consolidatedOrSeparate: 'Consolidated' | 'Separate'
  ): boolean {
    if (!control.ui.tableColumn) return false;

    const isRequired = control.required === true;
    const controlNames = ['Ordinary', 'Redeemable', 'NonRedeemable', 'Total'].map(member =>
      this.getXbrlControlName(control.qname, consolidatedOrSeparate, member)
    );

    let hasInvalid = false;
    let hasEmptyRequiredField = false;
    let hasBelowMin = false;
    for (const controlName of controlNames) {
      const formControl = this.form.get(controlName);
      if (formControl) {
        if (formControl.invalid && (formControl.dirty || formControl.touched)) {
          hasInvalid = true;
        }
        if (isRequired && (formControl.value === null || formControl.value === undefined || formControl.value === '')) {
          hasEmptyRequiredField = true;
        }
        // A negative share count blocks regardless of dirty/touched — mirrors
        // the headless pure validator's min check.
        if (valueBelowSchemaMin(control, formControl.value)) {
          hasBelowMin = true;
        }
      }
    }

    return hasInvalid || hasEmptyRequiredField || hasBelowMin;
  }

  /**
   * Get validation icon based on status
   */
  getValidationIcon(status: 'Failed' | 'Edited' | 'Validated*' | 'Validated'): string {
    switch (status) {
      case 'Validated':
      case 'Validated*':
        return 'pi pi-check-circle';
      case 'Failed':
        return 'pi pi-times-circle';
      case 'Edited':
        return 'pi pi-pencil';
      default:
        return '';
    }
  }

  /**
   * Get validation icon color
   */
  getValidationIconColor(status: 'Failed' | 'Edited' | 'Validated*' | 'Validated'): string {
    switch (status) {
      case 'Validated':
      case 'Validated*':
        return 'green';
      case 'Failed':
        return 'red';
      case 'Edited':
        return 'orange';
      default:
        return '';
    }
  }

  /**
   * Get all error messages for a row (from all 4 input columns including Total)
   * Groups errors by category: "Error" (red) and "* Suggested Change" (black)
   * Note: Role23 only has Error category (no API mismatch warnings)
   */
  /**
   * Evaluate sheet status by looping through all rows' mapping status.
   * Checks both Consolidated and Separate tables (if visible).
   * Sets hasErrors = true if any row has 'Failed' status, false otherwise.
   * Called at init and before submit.
   */
  evaluateSheetStatus(): void {
    if (!this.schema?.controls) return;

    let hasErrors = false;

    // Check non-table fields (e.g. required TextBlock) via form control validity
    for (const control of this.schema.controls) {
      if (!control.ui.tableColumn && control.required) {
        const formControl = this.form.get(control.qname);
        if (formControl && formControl.invalid) {
          hasErrors = true;
          break;
        }
      }
    }

    // Check table rows via mapping status
    if (!hasErrors) {
      const types: ('Consolidated' | 'Separate')[] = this.showConsolidatedTable ? ['Consolidated', 'Separate'] : ['Separate'];

      for (const type of types) {
        if (hasErrors) break;
        for (const control of this.schema.controls) {
          // Verdict uses the UNMASKED error check, so an in-edit-mode row with an
          // empty required cell still blocks (displays 'Edited', verdict true).
          if (this.rowHasBlockingError(control, type)) {
            hasErrors = true;

            // Debug Log (Only enable when required to debug, do not allow this to land into prod)
            // console.log("[On Notes - Issued Capital | Evaluate Sheet Status] This Schema Control Failed: ", control);

            break;
          }
        }
      }
    }

    // Debug Log (Only enable when required to debug, do not allow this to land into prod)
    // console.log("[On Notes - Issued Capital | Evaluate Sheet Status] Schema Controls: ", this.schema.controls);
    // console.log("[On Notes - Issued Capital | Evaluate Sheet Status] Has Errors: ", hasErrors);

    this.stateService.updateSheetLevelStatus(SHEET_KEYS.notesIssuedCapital, { hasErrors });
  }

  getRowErrorDescription(control: SchemaControl, consolidatedOrSeparate: 'Consolidated' | 'Separate'): string {
    if (!control.ui.tableColumn) return '';

    const inputMembers = ['Ordinary', 'Redeemable', 'NonRedeemable', 'Total'];
    const memberLabels: { [key: string]: string } = {
      'Ordinary': 'Ordinary shares',
      'Redeemable': 'Redeemable preference shares',
      'NonRedeemable': 'Non-redeemable preference shares',
      'Total': 'Total'
    };

    const errors: string[] = [];           // Red - Error category
    const suggestedChanges: string[] = []; // Black - * Suggested Change category (not used in Role23)

    // Check each column for required field with no value -> Error category
    const isRequired = control.required === true;

    // Collect form control validation errors and required field errors -> Error category
    for (const member of inputMembers) {
      const controlName = this.getXbrlControlName(control.qname, consolidatedOrSeparate, member);
      const formControl = this.form.get(controlName);

      if (formControl) {
        const errorMessages: string[] = [];

        // Check required field with no value (field by field)
        if (isRequired && (formControl.value === null || formControl.value === undefined || formControl.value === '')) {
          errorMessages.push('This is a mandatory field');
        }

        // Below-min (negative share count) — surfaced independent of
        // dirty/touched so a hydrated negative is described, mirroring the
        // pure validator. Deduped against the dirty/touched min branch below.
        if (valueBelowSchemaMin(control, formControl.value)) {
          const msg = `Minimum value: ${this.schemaMin(control)}`;
          if (!errorMessages.includes(msg)) {
            errorMessages.push(msg);
          }
        }

        // Check form control validation errors
        if (formControl.invalid && (formControl.dirty || formControl.touched)) {
          if (formControl.errors) {
            if (formControl.errors['required']) {
              // Only add if not already added as mandatory field
              if (!errorMessages.includes('This is a mandatory field')) {
                errorMessages.push('This is a mandatory field');
              }
            }
            if (formControl.errors['min']) {
              const msg = `Minimum value: ${formControl.errors['min'].min}`;
              if (!errorMessages.includes(msg)) {
                errorMessages.push(msg);
              }
            }
            if (formControl.errors['max']) {
              errorMessages.push(`Maximum value: ${formControl.errors['max'].max}`);
            }
          }
        }

        if (errorMessages.length > 0) {
          errors.push(`${memberLabels[member]}: ${errorMessages.join(', ')}`);
        }
      }
    }

    // Format output grouped by category with CSS classes
    // Error section in red (.error-text), Suggested Change section in black (.suggested-change-text)
    let result = '';

    if (errors.length > 0) {
      result += '<span class="error-text">ERROR</span><br>';
      result += '<span class="error-text">' + errors.map((errorText, errorIndex) => `${errorIndex + 1}. ${errorText}`).join('<br><br>') + '</span>';
    }

    if (suggestedChanges.length > 0) {
      if (result) result += '<br><br>';
      result += '<span class="error-text">*</span><span class="suggested-change-text"> SUGGESTED CHANGE</span><br>';
      result += '<span class="suggested-change-text">' + suggestedChanges.map((changeText, changeIndex) => `${changeIndex + 1}. ${changeText}`).join('<br><br>') + '</span>';
    }

    return result;
  }

  /**
   * Check if row has any "Error" category errors (red text)
   * Error category includes: form validation errors
   */
  hasErrorCategory(control: SchemaControl, consolidatedOrSeparate: 'Consolidated' | 'Separate'): boolean {
    if (!control.ui.tableColumn) return false;

    const inputMembers = ['Ordinary', 'Redeemable', 'NonRedeemable', 'Total'];
    const isRequired = control.required === true;

    for (const member of inputMembers) {
      const controlName = this.getXbrlControlName(control.qname, consolidatedOrSeparate, member);
      const formControl = this.form.get(controlName);

      if (formControl) {
        // Check for invalid controls that have been interacted with
        if (formControl.invalid && (formControl.dirty || formControl.touched)) {
          return true;
        }

        // Check for empty required fields (same as getRowValidationStatus)
        // This ensures sheet-level status shows Failed when required fields are empty
        if (isRequired && (formControl.value === null || formControl.value === undefined || formControl.value === '')) {
          return true;
        }
      }
    }

    return false;
  }

  /**
   * Check if row has any "Suggested Change" category errors (black text)
   * Suggested Change category includes: API value mismatch warnings
   * Note: Role23 does not have API mismatch validation, always returns false
   */
  hasSuggestedChangeCategory(control: SchemaControl, consolidatedOrSeparate: 'Consolidated' | 'Separate'): boolean {
    // Role23 does not have validationErrors Map for API mismatch warnings
    return false;
  }

  /**
   * Check if a row has failed validation (for hover styling)
   */
  hasRowFailed(control: SchemaControl, consolidatedOrSeparate: 'Consolidated' | 'Separate'): boolean {
    return this.getRowValidationStatus(control, consolidatedOrSeparate) === 'Failed';
  }

  /**
   * Check if a specific column has an error (null value in required field)
   * Used to apply red border styling to individual input fields
   *
   * @param control - The schema control for the row
   * @param consolidatedOrSeparate - Dimension (Consolidated or Separate)
   * @param member - Share class identifier ('Total', 'Ordinary', 'Redeemable', 'NonRedeemable')
   * @returns true if the column has an error (required field is null/empty)
   */
  hasColumnError(control: SchemaControl, consolidatedOrSeparate: 'Consolidated' | 'Separate', member: string): boolean {
    if (!control.ui.tableColumn) return false;

    const isRequired = control.required === true;
    if (!isRequired) return false;

    const controlName = this.getXbrlControlName(control.qname, consolidatedOrSeparate, member);
    const formControl = this.form.get(controlName);

    if (!formControl) return false;

    // Check if required field has no value (null, undefined, or empty string)
    return formControl.value === null || formControl.value === undefined || formControl.value === '';
  }

  /**
   * Build the per-dimension edit-mode key for a row. The Consolidated and Separate
   * tables render the same `control` (same qname); namespacing the key by dimension
   * keeps their edit state independent so the pencil in one table no longer flips the
   * same-named row in the other. Mirrors role19's getEditKey.
   */
  private getEditKey(control: SchemaControl, dimension: 'Consolidated' | 'Separate'): string {
    return `${control.qname}|${dimension}`;
  }

  /**
   * The 4 editable input controls (Ordinary, Redeemable, NonRedeemable, Total) for a
   * single dimension of a row — the unit that one edit session enables/reverts. Only
   * this dimension's cells participate, never the other table's.
   */
  private editableControlPaths(control: SchemaControl, dimension: 'Consolidated' | 'Separate'): string[] {
    return ['Ordinary', 'Redeemable', 'NonRedeemable', 'Total'].map(member =>
      this.getXbrlControlName(control.qname, dimension, member)
    );
  }

  /**
   * Check if a row is currently in edit mode for the given dimension.
   */
  isRowInEditMode(control: SchemaControl, dimension: 'Consolidated' | 'Separate'): boolean {
    return this.editingRows.has(this.getEditKey(control, dimension));
  }

  /**
   * Enter edit mode for one dimension of a row - store original values, enable controls.
   * Scope is the single dimension the user clicked into, not both tables.
   */
  enterEditMode(control: SchemaControl, dimension: 'Consolidated' | 'Separate'): void {
    // Don't allow editing before validation is complete
    if (!this.isValidationComplete) {
      return;
    }
    // Don't allow editing calculated or readonly fields
    if (control.ui.readonly || control.ui.calculated) {
      return;
    }

    // Capture the committed badge BEFORE entering edit mode (isRowInEditMode is still false here).
    this.frozenRowStatus.set(this.getEditKey(control, dimension), this.getRowValidationStatus(control, dimension));

    // Store original values for this dimension's 4 input controls (including Total)
    const originalValues: { [key: string]: number | null } = {};
    const controlPaths = this.editableControlPaths(control, dimension);

    controlPaths.forEach(path => {
      const formControl = this.form.get(path);
      if (formControl) {
        // Store the current value (FormControl.value returns number | null)
        const currentValue = formControl.value;
        originalValues[path] = currentValue ?? null;

        // Enable the control for editing
        // Use {emitEvent: false} to prevent triggering auto-save when entering edit mode
        enableAndRevealErrors(formControl);
      }
    });

    this.editingRows.set(this.getEditKey(control, dimension), originalValues);
    this.stateService.markSheetEditing(SHEET_KEYS.notesIssuedCapital);
  }

  /**
   * Save edit mode - validate, save to localStorage, disable controls
   * Checks if values differ from original API data and marks as 'Edited'
   */
  saveEditMode(control: SchemaControl, dimension: 'Consolidated' | 'Separate'): void {
    if (!this.editingRows.has(this.getEditKey(control, dimension))) {
      return;
    }

    // Get this dimension's 4 input controls for this row (including Total)
    const controlPaths = this.editableControlPaths(control, dimension);

    // Check if any control has validation errors
    const hasErrors = controlPaths.some(path => {
      const formControl = this.form.get(path);
      return formControl && formControl.invalid;
    });

    if (hasErrors) {
      this.messageService.add({
        severity: 'error',
        summary: 'Validation Error',
        detail: 'Please fix validation errors before saving'
      });
      return;
    }

    // Disable this dimension's input controls and exit edit mode
    // Use {emitEvent: false} to prevent triggering extra auto-saves
    controlPaths.forEach(path => {
      const formControl = this.form.get(path);
      if (formControl) {
        formControl.disable({ emitEvent: false });
      }
    });

    this.frozenRowStatus.delete(this.getEditKey(control, dimension));
    this.editingRows.delete(this.getEditKey(control, dimension));

    // Commit: persist ONLY this row+dimension's cells (subset merge).
    const subset = pickSubset(this.form.getRawValue(), controlPaths);
    this.stateService.updateSheetData(SHEET_KEYS.notesIssuedCapital, subset);
    this.stateService.reconcileEditedFlag(SHEET_KEYS.notesIssuedCapital);
    this.stateService.clearSheetEditing(SHEET_KEYS.notesIssuedCapital);
    this.evaluateSheetStatus();
  }

  /**
   * Cancel edit mode - always reverts to the snapshot stored when entering edit mode.
   * Scoped to the dimension the user is editing.
   */
  cancelEditMode(control: SchemaControl, dimension: 'Consolidated' | 'Separate'): void {
    const editKey = this.getEditKey(control, dimension);
    if (!this.editingRows.has(editKey)) {
      return;
    }

    // Get this dimension's 4 input controls for this row
    const controlPaths = this.editableControlPaths(control, dimension);

    // Revert to the LATEST ADOPTED state rather than the entry-time snapshot, so a
    // sibling tab's confirmed edit that arrived while this row was open is honoured
    // on cancel (P.O. rule 2026-08-17). Falls back to the entry snapshot for any
    // control the state does not carry.
    //
    // In the single-tab case the two are identical — the row is disabled and
    // unwritten for the whole time it is open — so this is a provable no-op there.
    const originalValues = this.editingRows.get(editKey);
    const adopted = this.stateService.getSheetData(SHEET_KEYS.notesIssuedCapital);
    if (originalValues) {
      controlPaths.forEach((controlName) => {
        const formControl = this.form.get(controlName);
        if (formControl) {
          const revertValue = controlName in adopted
            ? adopted[controlName]
            : (originalValues[controlName] ?? null);
          formControl.setValue(revertValue, { emitEvent: false });
          formControl.markAsPristine();
          formControl.disable({ emitEvent: false });
        }
      });
    }

    // Exit edit mode
    this.frozenRowStatus.delete(editKey);
    this.editingRows.delete(editKey);
    this.stateService.clearSheetEditing(SHEET_KEYS.notesIssuedCapital);
    this.evaluateSheetStatus();
  }

  onCancelConfirmed(): void {
    // Clear all editing states
    this.editingRows.clear();
    this.frozenRowStatus.clear();
    this.stateService.clearSheetEditing(SHEET_KEYS.notesIssuedCapital);

    // Reset validation complete flag before reloading
    this.isValidationComplete = false;

    // Reload from the reverted state service data
    this.loadSavedState();
    this.loadTraceHandle?.mark(SHEET_LOAD_STEPS.stateRestored);
    this.form.markAsPristine();
  }
}
